import React, { Component } from 'react';
import { observer } from 'mobx-react';
import {
  VerticalProgress,
  PushButton,
} from '../components';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import {
  Sound,
  Sounds,
} from '../../Sound';
import { toNumber } from '../../../utils/numbers';
import convertUnit from '../../../utils/unit-converter';
import {
  InventoryLabel,
  TargetLabel,
  BalanceLabel,
  DeviceStatusLabel,
  CapacityLabel,
} from '../components/labels';
import { BinSwap } from '../components/BinSwap';
import { BinActivation } from '../components/BinActivation';
import MaterialVisibilityChange from '../components/MaterialVisibilityChange';
import {
  getRunMode,
  isRunModeNewYork,
} from '../../../stores/batch/run-mode';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import { ToleranceMode } from '../../../constants/Configuration';
const FEATURE_DEFER_BINS = isFeatureEnabled('defer_bins');
const FEATURE_SWAP_MATERIAL_MID_BATCH = isFeatureEnabled('swap_material_mid_batch');

@observer
class SiloBin extends Component {
  handleShowSubstitutionSidebar = () => {
    const { device, material, batchStore, sidebarStore } = this.props;
    const scale = batchStore.devicesStore.getDeviceById(device.scaleId);
    const currentScaleWeight = scale ? batchStore.getScaleWeight(scale) : 0;

    // Set the substitution data in batchStore
    batchStore.setMaterialSubstitutionData({
      originalMaterialId: material?.itemId,
      scaleId: scale?.id,
      currentWeight: currentScaleWeight,
    });

    // Open the sidebar
    sidebarStore.onToggle('materialSubstitution');
  };

  render() {
    const { device, batchStore, material, materialDetails, settingsStore, materialStore, onPush, onPushUp, holdFeed, finishFeed, isBinForMultiScale, deferFeed, isDeferFeedDisabled, hasBeenSubstituted, substitutionWeight } = this.props;
    const { configuration } = settingsStore;
    const active = batchStore.isOutputOn(device.gateOnePNo);
    const cn = active ? 'gate-open' : 'gate-close';
    const deviceStatus = batchStore.getDeviceStatus(device.id);
    const { status, target, error, unit, weight, materialCode } = deviceStatus;
    const runMode = getRunMode(configuration, batchStore);
    const isError = error ? 'error' : '';
    const md = materialDetails || {};
    const isBinActive = md.bins?.some(v => v.isActive && materialStore.binMatchesId(v, device.id));
    const binBalance = material
      ? isBinActive
        ? materialStore.getActiveBinsBalance(material)
        : materialStore.getBinBalance(material, device.id)
      : 0;
    const name = materialCode || (material ? materialStore.getMaterialName(material) : '');
    const batchUnit = unit || md.itemBatchUnitSymbol;
    const inventoryUnit = material ? material.itemInventoryUnitSymbol : '';
    const scale = batchStore.devicesStore.getDeviceById(device.scaleId);
    const deviceUnit = scale && scale.unitId ? batchStore.getUnitById(scale.unitId) : batchUnit;
    const isDeferenceUnit = batchUnit ? batchUnit !== deviceUnit : false;
    let convertedTarget = target;
    const isCumulative = isRunModeNewYork(runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative;
    if (hasBeenSubstituted && !deviceStatus.targetIsNet && !isCumulative) {
      convertedTarget -= substitutionWeight;
    }
    const progress = convertedTarget > 0 && weight > 0 ? weight / convertedTarget * 100 : 0;
    const tolerance = convertedTarget > 0 ? batchStore.getToleranceByPercent(md, convertedTarget) : 0;
    let convertedWeight = weight;
    const isMoreBins = md?.bins?.filter(v => !v.isPrimary && v.isActive).length === 1;
    const isSecondaryBin = md?.bins?.some(v => !v.isPrimary && v.binId === device.id);
    const isLinkedBin = md?.bins?.some(v => v.linkedBinId === device.id);
    const inputDisabled = batchStore.isInputDisabled(device.gateCloseStatusPNo) ? 'device-input-disabled' : '';
    const isBinDisabled = md.bins?.some(v => !v.isActive && (v.binId === device.id || v.linkedBinId === device.id)) ? 'bin-disabled' : '';
    const isBinDeferred = batchStore.isDeviceDeferred(device.scaleId, device.id);
    const noScaleError = !scale ? 'no-scale-error' : '';
    const isFeedMode = batchStore.isHoldFeedDevice(device.id)
      || (
        [DeviceStatus.FEED, DeviceStatus.JOG, DeviceStatus.HOLD, DeviceStatus.SETTLING, DeviceStatus.OVER].includes(deviceStatus.status)
        && batchStore.running.length
      );
    const noSwapBins = [DeviceStatus.FEED, DeviceStatus.JOG, DeviceStatus.SETTLING].includes(deviceStatus.status)
      && batchStore.running.length;
    const holdFeedIcon = batchStore.isHoldFeedDevice(device.id) ? 'play' : 'pause';

    // Check if material substitution is available
    const canSubstituteMaterial = FEATURE_SWAP_MATERIAL_MID_BATCH && material && scale && batchStore.running.length &&
      [DeviceStatus.FEED, DeviceStatus.JOG, DeviceStatus.HOLD, DeviceStatus.SETTLING].includes(status);

    if (isDeferenceUnit) {
      convertedTarget = toNumber(convertUnit(convertedTarget, deviceUnit, batchUnit), 0);
      convertedWeight = toNumber(convertUnit(weight, deviceUnit, batchUnit), 0);
    }

    const binType = isSecondaryBin ? 'secondary' : 'primary';
    const otherMaterials = materialStore.getMaterialsWithTheSameOutput(device.id);

    return (
      <div id={`device-${device.id}`} className={`${cn} status-${status} device-gui device-gui--silo-bin device-${isError} ${inputDisabled} ${isBinDisabled} ${noScaleError} device-icon-${device.icon}`}>
        <Sound file={Sounds.alarm} play={isError} loop={true}/>
        <MaterialVisibilityChange
          materials={otherMaterials}
          material={material}
          onMaterialClick={m => materialStore.makeMaterialVisible(m, device.id)}
        />
        <DeviceStatusLabel
          name={name}
          status={status}
          description={device.description}
          title={`#${device.id} ${binType} ${inputDisabled} ${noScaleError}`}
          displayIcon={noScaleError}
        />
        {!!md.showBalance &&
          <InventoryLabel balance={binBalance} decimal={1} unit={inventoryUnit}/>
        }
        {!!md.showCapacity &&
          <CapacityLabel capacity={device.capacity} decimal={1} unit={inventoryUnit}/>
        }
        <div className="device-icon body"/>
        <div className="device-icon gate"/>
        <div className="device-label device-label-target">
          <TargetLabel target={convertedTarget} unit={batchUnit} icon={isSecondaryBin ? 'fa' : 'fa fa-crosshairs'} padZeros={scale?.graduation > 1}/>
          <BalanceLabel balance={convertedWeight} unit={batchUnit} padZeros={scale?.graduation > 1}/>
        </div>

        {!isLinkedBin && !noSwapBins && !isSecondaryBin && material && isMoreBins && (
          <BinSwap onChange={() => {
            materialStore.swapBins(device.id, material, md);
          }}/>
        )}
        {!isLinkedBin && !noSwapBins && isSecondaryBin && material && (
          <BinActivation activeClassName={isBinDisabled} onChange={() => {
            materialStore.toggleBin(device.id, material, md);
          }}/>
        )}

        {/* Material Substitution Button */}
        {canSubstituteMaterial && !isSecondaryBin && batchStore.isHoldFeedDevice(device.id) && (
          <button
            className="btn-on btn-batch btn-batch-orange btn-substitute-material"
            data-e2e-id={`btn-device-substitute-material-${device.id}`}
            title="Substitute Material"
            onClick={this.handleShowSubstitutionSidebar}
          >
            <i className="fa fa-exchange"/>
          </button>
        )}

        <div className={'device-icon particle'}/>

        {(!isSecondaryBin || isBinForMultiScale) && !isRunModeNewYork(runMode) && !!isFeedMode && !batchStore.isHoldFeed && (
          <button className={'btn-on btn-batch btn-batch-red btn-hold-feed'}
            title={batchStore.isHoldFeedDevice(device.id) ? 'Start feed' : 'Hold feed'}
            disabled={isBinDeferred}
            onClick={holdFeed}>
            <i className={`fa fa-${holdFeedIcon}`}/>
          </button>
        )}

        {(!isSecondaryBin || isBinForMultiScale) && !isRunModeNewYork(runMode) && !!isFeedMode && batchStore.isHoldFeedDevice(device.id) && (
          <button className={'btn-on btn-batch btn-batch-red btn-finish-feed'}
            title="Finish feed"
            onClick={e => finishFeed(e, material)}>
            <i className={'fa fa-stop'}/>
          </button>
        )}

        {FEATURE_DEFER_BINS && !hasBeenSubstituted && !!isFeedMode && batchStore.isHoldFeed && (
          <button
            className="btn-on btn-batch btn-batch-red btn-batch-defer"
            title="Defer this material"
            onClick={deferFeed}
            disabled={isDeferFeedDisabled}
          >
            <i className="fa fa-forward"/>
          </button>
        )}

        {(isBinDisabled && batchStore.running.length) || !device.gateOnePNo || (!configuration.manualFeed && batchStore.running.length) ? '' : (
          <PushButton port={batchStore.maintenanceMode && !configuration.reduceMaintenanceModeInventory ? null : device.gateOnePNo}
            onPush={onPush}
            onPushUp={onPushUp}
            disabled={isRunModeNewYork(runMode)}
            onRightClickHoldLabel={'Hold to keep open..'}
            onRightClickHoldEnd={onPush}
            rightClickHoldDuration={1250}
          />
        )}

        <VerticalProgress
          showProgress={true}
          showPercent={true}
          tolerance={tolerance}
          value={progress}
          active={active}
        />
      </div>
    );
  }
}

export default SiloBin;
